import React from 'react';
import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Stack from '@mui/material/Stack';
import LayersIcon from '@mui/icons-material/Layers';
import SideMenuIconButton from '../common/SideMenuIconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';

import NumericInput from '../../common/inputs/NumericInput';
import PrestaGlobals from "../../common/PrestaGlobals";

interface DesktopInstacartTableRowProps {
    isRemainder?: boolean;
    edgingIcon?: React.ReactNode;
    name: string;
    productImage?: React.ReactNode;
    materialName?: string;
    isDuplunged?: boolean;
    dimensions?: string;
    price?: number;
    priceFinal?: boolean;
    count: number;
    open?: boolean;
    doIndent?: boolean;
    isEditing?: boolean;
    expandClick?: () => void;
    updateCount?: (newCount: number) => void;
    deleteItem?: () => void;
    editItem?: () => void;
    disabled?: boolean;
}

/** Instacart table row component */
const DesktopInstacartTableRow: React.FC<DesktopInstacartTableRowProps> = (props) => {
    const theme = useTheme();
    return (
        <TableRow>
            <TableCell onClick={props.expandClick} style={{ cursor: props.expandClick ? 'pointer' : 'inherit' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" style={{ marginLeft: props.doIndent ? '75px' : '0' }}>
                        {props.open !== undefined && (
                            <SideMenuIconButton
                                isOpen={props.open}
                                onClick={props.expandClick!}
                                size="small"
                                ariaLabel={props.open ? 'Collapse' : 'Expand'}
                            />
                        )}
                        {props.productImage || <></>}
                        {props.edgingIcon || <></>}
                        <Box display="flex" alignItems="center" >
                            <Typography variant="subtitle1">
                                {props.name}
                                {props.materialName !== undefined && (
                                    <>
                                    <br/><small>{props.materialName}</small>
                                    </>
                                )}
                            </Typography>

                        </Box>
                    </Box>
                    {props.isDuplunged && (
                        <LayersIcon />
                    )}
                </Stack>
            </TableCell>
            <TableCell>
                {props.dimensions !== undefined && (
                    <Typography variant="subtitle2" title={props.dimensions}>{props.isRemainder ? (Number(props.dimensions) / 10000).toFixed(2)+"m²" : props.dimensions+"mm"}</Typography>
                )}
            </TableCell>
            <TableCell>
                {props.price !== undefined && (
                    <Typography variant="subtitle2" style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                        <span>{props.price} Ft</span>
                        </Typography>
                )}
            </TableCell>
            <TableCell>
                {props.updateCount !== undefined
                ?
                    <NumericInput
                        value={props.count}
                        min={1}
                        max={1000}
                        smallStep={1}
                        largeStep={1}
                        editable={!props.disabled}
                        readOnly={props.disabled ?? false}
                        onValueChanged={(newValue: number) => props.updateCount!(newValue)}
                        unitLabel="db"
                        slotProps={{
                            textField: {
                                sx: {
                                    backgroundColor: '#ffffff !important',
                                    borderRadius: '4px 0 0 4px',
                                    '& .MuiInputBase-root': {
                                        backgroundColor: '#ffffff !important',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#000 !important',
                                        WebkitTextFillColor: '#000 !important',
                                    },
                                }
                            },
                            buttonGroupButton: {
                                color: `${theme.palette.primary.contrastText} !important`,
                                backgroundColor: `${theme.palette.primary.main} !important`,
                                borderColor: '#000 !important',
                                '&:hover': {
                                    color: `${theme.palette.primary.contrastText} !important`,
                                    backgroundColor: `${theme.palette.primary.main} !important`,
                                }
                            }
                        }}
                    />
                :
                    <Typography variant="subtitle2">{props.count} db</Typography>
                }
            </TableCell>
            <TableCell>
                <ButtonGroup disabled={props.disabled}>
                    {props.editItem !== undefined && (
                        <Button>
                            {props.isEditing ? (
                                <EditOffIcon onClick={props.editItem!} />
                            ) : (
                                <EditIcon onClick={props.editItem!} />
                            )}
                        </Button>
                    )}
                    {props.deleteItem !== undefined && (
                        <Button>
                            <DeleteIcon onClick={props.deleteItem!} />
                        </Button>
                    )}
                </ButtonGroup>
            </TableCell>
        </TableRow>
    );
}

export default DesktopInstacartTableRow;
